import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import s from './DropdownMenu.module.css';

export const DROPDOWN_MENU_PORTAL_ATTR = 'data-dropdown-menu-portal';

function computePortalPosition(anchorEl, menuEl, align) {
  const anchor = anchorEl.getBoundingClientRect();
  const menuW = menuEl?.offsetWidth || 150;
  const menuH = menuEl?.offsetHeight || 88;
  const gap = 6;
  const safe = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let left = align === 'left' ? anchor.right - menuW : anchor.left;
  let top = anchor.bottom + gap;

  if (top + menuH > vh - safe) {
    top = anchor.top - gap - menuH;
  }
  if (left + menuW > vw - safe) {
    left = vw - safe - menuW;
  }
  if (left < safe) left = safe;
  if (top < safe) top = safe;

  return { left, top };
}

export function DropdownMenu({
  open = false,
  items = [],
  align = 'right',
  className = '',
  onMenuClick,
  role = 'menu',
  anchorRef = null,
  portal = false,
  portalZIndex = 13050,
}) {
  const menuRef = useRef(null);
  const [portalStyle, setPortalStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open || !portal || !anchorRef?.current) {
      setPortalStyle(null);
      return undefined;
    }

    const update = () => {
      if (!anchorRef.current) return;
      const { left, top } = computePortalPosition(
        anchorRef.current,
        menuRef.current,
        align,
      );
      setPortalStyle({
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        right: 'auto',
        zIndex: portalZIndex,
      });
    };

    update();
    const raf = requestAnimationFrame(update);

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, portal, anchorRef, align, items.length, portalZIndex]);

  if (!open || !items.length) return null;

  const alignClass = !portal && (align === 'left' ? s.dropLeft : s.dropRight);
  const fallbackPortalStyle = portal && anchorRef?.current && !portalStyle
    ? (() => {
      const { left, top } = computePortalPosition(anchorRef.current, null, align);
      return {
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        right: 'auto',
        zIndex: portalZIndex,
      };
    })()
    : null;

  const menuNode = (
    <div
      ref={menuRef}
      className={`${s.menu} ${portal ? s.menuPortal : ''} ${alignClass || ''} ${className}`.trim()}
      style={portal ? (portalStyle || fallbackPortalStyle) : undefined}
      {...(portal ? { [DROPDOWN_MENU_PORTAL_ATTR]: '' } : {})}
      onClick={onMenuClick || ((e) => e.stopPropagation())}
      role={role}
    >
      {items.map((item) => (
        <button
          key={item.key || item.label}
          type="button"
          role="menuitem"
          className={`${s.item} ${item.danger ? s.danger : ''}`.trim()}
          onClick={item.onClick}
          disabled={item.disabled}
        >
          {item.label}
        </button>
      ))}
    </div>
  );

  if (portal && typeof document !== 'undefined') {
    return createPortal(menuNode, document.body);
  }

  return menuNode;
}
